import React, { useEffect, useState } from 'react';
import './Login.scss';
import login from '../../../assets/image/login_illustration.svg';
import InputField from '../../../component/form/FormInput';
import Button from '../../../component/ui/Button';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ModalRight from '../../../component/modalRight';
import logo from '../../../assets/image/onboarding-logo.png';
import pharmnexDefaultLogo from '../../../assets/image/PharmaNex_small.svg';
import useAuth from '../../../hooks/useAuth';
import { fetchOrganisationBranding } from '../../../services/auth';
import BackButton from '../../../component/backbutton/BackButton';
import { useSelector } from 'react-redux';

const Login = () => {
	const user = useSelector((state) => state.userInfo);
	const isAuthenticated = user.isAuthenticated;
	const location = useLocation();
	const navigate = useNavigate();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [loader, setLoader] = useState(false);
	const [error, setError] = useState({});
	const [errorSubDoman, setErrorSubDoman] = useState('');
	const [openPassPolicy, setOpenPassPolicy] = useState(false);
	const { userLogin, userOnboardingMe, checkSsoSession } = useAuth();
	const [searchParams] = useSearchParams();
	const [branding, setBranding] = useState(null);
	const [orgSubdomain, setOrgSubDomain] = useState(null);

	const handleSubmit = async (e) => {
		setLoader(true);
		setError({});
		try {
			const data = await userLogin({ email, password, subdomain: orgSubdomain });
			if (data.success) {
				await userOnboardingMe({
					email: data.data.email,
					redirectParam:
						location.pathname.match(/^\/user\/[^/]+\/?(.*)$/)?.[1] || searchParams.get('redirect'),
				});
			} else {
				if (data.message === 'Invalid organisation login.') {
					window.history.replaceState(null, '', `${window.location.origin}`);
					setOrgSubDomain(null);
					setBranding(null);
				}
			}
		} catch (error) {
		} finally {
			setLoader(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			handleSubmit(e);
		}
	};

	const handleFetchBranding = async () => {
		const extractSubDomainFromPath = (pathname) => pathname.match(/^\/user\/([^/]+)/)?.[1] || null;
		const subDomain = extractSubDomainFromPath(location.pathname);
		if (!subDomain) {
			setErrorSubDoman('');
			return;
		}

		const data = await fetchOrganisationBranding(subDomain);
		if (!data.success) {
			setErrorSubDoman('Invalid Organisation Login');
		} else {
			setErrorSubDoman('');
			setBranding(data.data?.organisation || null);
		}
		setOrgSubDomain(subDomain || null);
		setBranding(data.data?.organisation || null);
	};

	useEffect(() => {
		document.title = 'Login | Pharmnex';
		const redirectUrl = location.pathname.match(/^\/user\/[^/]+\/?(.*)$/)?.[1] || '';
		if (!isAuthenticated && !searchParams.get('redirect') && redirectUrl !== '') {
			const subDomain = location.pathname.match(/^\/user\/([^/]+)/)?.[1];
			const redirectUrl = location.pathname.match(/^\/user\/[^/]+\/?(.*)$/)?.[1] || '';

			subDomain &&
				window.history.replaceState(
					null,
					'',
					`${window.location.origin}/user/${subDomain}?redirect=${redirectUrl}`,
				);
		}
		handleFetchBranding();
	}, []);

	useEffect(() => {
		if (isAuthenticated && user?.userInfo?.data?.sub_domain) {
			const redirectParam = searchParams.get('redirect');
			const targetPath = redirectParam
				? `/user/${user.userInfo.data.sub_domain}/${redirectParam}`
				: `/user/${user.userInfo.data.sub_domain}/home`;
			navigate(targetPath, { replace: true });
		} else if (!isAuthenticated) {
			const subDomain = location.pathname.match(/^\/user\/([^/]+)/)?.[1];
			checkSsoSession(subDomain)
				.then((res) => {
					if (res?.success && res?.authenticated && res?.token) {
						const finalSub = res.data?.organisation?.sub_domain || subDomain;
						const redirectParam = searchParams.get('redirect');
						const targetPath =
							redirectParam && finalSub
								? `/user/${finalSub}/${redirectParam}`
								: finalSub
									? `/user/${finalSub}/home`
									: '/';
						navigate(targetPath, { replace: true });
					}
				})
				.catch((err) => {
					console.error('SSO session check in Login failed:', err);
				});
		}
	}, [isAuthenticated, user?.userInfo?.data?.sub_domain, checkSsoSession, searchParams, location.pathname, navigate]);

	return (
		<div>
			<div
				className="mobile-login-header"
				style={{ background: '#EDEDF7', paddingTop: '10px' }}
			>
				<BackButton />
				<h1 className="mobile-title">Login</h1>
			</div>
			{errorSubDoman !== '' ? (
				<div
					className="d-flex justify-content-center align-items-center"
					style={{ height: '100vh' }}
				>
					<div className="fs-4 fw-bold text-dark">{errorSubDoman}</div>
				</div>
			) : (
				<div>
					<div className="login-form">
						<div className="login-body row clearfix">
							<div className="left-section">
								<div className="illustration">
									<div className="brand-name">
										<img
											src={logo}
											alt=""
										/>
									</div>
									<div className="login-illustration">
										<img
											src={login}
											alt=""
										/>
									</div>
								</div>
							</div>

							<div className="right-section">
								<div className="form-body py-4 ms-0">
									{branding && (
										<div className="brand-name">
											<img
												src={
													branding && branding.organisation_logo
														? branding.organisation_logo
														: pharmnexDefaultLogo
												}
												alt=""
												style={{
													width: '70px',
													height: '70px',
													objectFit: 'fill',
													marginBottom: '1rem',
													borderRadius: 20,
												}}
											/>
										</div>
									)}

									<h1 className="form-title">Login</h1>

									<InputField
										value={email}
										label="Email"
										placeholder="Registered Email Address"
										type="email"
										onChange={(value) => setEmail(value)}
										error={error?.email}
										required
										name="email"
										autoFocus
										autoComplete="email"
										onKeyDown={handleKeyDown}
									/>

									<InputField
										value={password}
										label="Password"
										placeholder="Password"
										type="password"
										onChange={(value) => setPassword(value)}
										error={error?.password}
										required
										name="password"
										autoComplete="current-password"
										onKeyDown={handleKeyDown}
										showPasswordIcon
										onClick={() => setOpenPassPolicy(true)}
									/>

									<label className="form-forgotPass">
										<Link to="/forgotPassword">Forgot Password?</Link>
									</label>
								</div>

								<Button
									className="btn-primary"
									id="signin-btn"
									button="Sign in"
									loader={loader}
									onClick={handleSubmit}
								/>

								<p className="mt-lg-2 mt-3 mt-sm-3 mb-3 text-center signin-btn">
									Don’t have a PharmNex account?
									<span className="primary-color cursor-pointer font-bold">
										<Link to="/signup"> Sign up </Link>
									</span>
									now
								</p>
							</div>
						</div>
					</div>

					{openPassPolicy && (
						<ModalRight
							show
							isOpen
							onClose={() => setOpenPassPolicy(false)}
							title="Password Policy"
							description1="Use a strong password with letters and numbers."
							description2="Minimum 8 characters required."
							description3="Avoid using personal information."
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default Login;
